import { useQuery } from '@tanstack/react-query'

import { CommonService } from '@/services/common.service'

import { IOption } from '@/types/selectOption.interface'

interface MetadataResponse {
	categories: Record<string, string>
	regiones: Record<string, Record<string, string>>
}

type CountriesByRegion = Record<string, IOption[]>

interface IMetadataOptions {
	categoriesOptions: IOption[]
	regionsOptions: IOption[]
	countriesOptions: CountriesByRegion
}

const METADATA_CACHE_TTL = 1000 * 60 * 60 * 4

const DEFAULT_METADATA: IMetadataOptions = {
	categoriesOptions: [],
	regionsOptions: [],
	countriesOptions: {}
}

const transformMetadata = (response: MetadataResponse): IMetadataOptions => {
	let categoriesOptions: IOption[] = []
	let regionsOptions: IOption[] = []
	let countriesOptions: CountriesByRegion = {}

	if (response?.categories && typeof response.categories === 'object') {
		const categories: IOption[] = Object.entries(response.categories)
			.filter(([label]) => label && label !== 'False')
			.map(([label, value]) => ({
				label,
				value
			}))
		categoriesOptions = categories
	}

	if (response?.regiones && typeof response.regiones === 'object') {
		// Transform regions
		const regions: IOption[] = Object.entries(response.regiones)
			.filter(([, countries]) => countries && Object.keys(countries).length > 0)
			.map(([region]) => ({ label: region, value: region }))
		regionsOptions = regions

		// Transform countries
		const countriesByRegion: CountriesByRegion = Object.fromEntries(
			Object.entries(response.regiones).map(([region, countriesObj]) => [
				region,
				Object.entries(countriesObj).map(([country, code]) => ({
					label: country,
					value: code
				}))
			])
		)
		countriesOptions = countriesByRegion
	}

	return {
		categoriesOptions,
		regionsOptions,
		countriesOptions
	}
}

export const useMetadata = () => {
	const { data = DEFAULT_METADATA } = useQuery({
		queryKey: ['uploadFormMetadata'],
		queryFn: () => CommonService.getMetadata(),
		staleTime: METADATA_CACHE_TTL,
		cacheTime: METADATA_CACHE_TTL,
		select: transformMetadata,
		onError: error => {
			console.error('Error loading metadata:', error)
		}
	})

	return data
}
